import Skeleton from '@mui/material/Skeleton';
import moment from 'moment';
import { type JSX, useEffect, useMemo, useState } from 'react';

import { BarChart } from 'Components/BaseCharts/BarChart';
import { CSSColors } from 'Components/Colors';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { ChangeEventType } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { CustomDateRangeModal } from 'Components/Modal/CustomDateRangeModal/CustomDateRangeModal';
import { Text } from 'Components/Text/Text';
import { toTitleCase } from 'Helpers/StringUtils';
import { useDateFilter } from 'Hooks/DateFilter';
import { DateOptionSelectOption, DefaultUpcomingDateRangeOptions, IssuesExceptionsDashboardType } from 'Models/Dashboards';
import { ExceptionResponse, ExceptionRiskScore, ExceptionRiskScoreFilter, ExceptionRiskScoreFilterOptions, ExceptionStatus, isValidExceptionRiskScoreFilter } from 'Models/Exceptions';
import { OrganizationResponseBase } from 'Models/Organization';
import { getMonths, getOpenExceptionsPerMonth, getProductModuleForDashboardType } from 'Pages/Dashboards/Helpers/Dashboards.helpers';

import styles from './DashboardsTrackingExceptions.module.css';
import { TrackingExceptionsDisplayModal } from './TrackingExceptionsDisplayModal/TrackingExceptionsDisplayModal';

export interface DashboardsTrackingExceptionsProps {
    allExceptions?: ExceptionResponse[];
    dashboardType: IssuesExceptionsDashboardType;
    exceptionsError?: string;
    organizations?: OrganizationResponseBase[];
    organizationsError?: string;
}

enum Modals {
    NONE,
    DISPLAY_OPEN_EXCEPTIONS,
    CUSTOM_DATE,
}

/**
 * Renders a bar chart that displays, over a given time period, and grouped by month, the number of exceptions that are coming due.
 * The first bar shows overdue exceptions.
 */
export const DashboardsTrackingExceptions = (props: DashboardsTrackingExceptionsProps): JSX.Element => {
    const dashboardExceptions = useMemo(() => {
        if (!props.allExceptions) return undefined;
        const moduleType = getProductModuleForDashboardType(props.dashboardType);
        return props.allExceptions.filter((exception) => exception.type === moduleType && (exception.status === ExceptionStatus.APPROVED || exception.status === ExceptionStatus.DRAFT_CLOSE));
    }, [props.allExceptions, props.dashboardType]);

    const [exceptionsByMonth, setExceptionsByMonth] = useState<number[]>();
    const [monthLabel, setMonthLabel] = useState<string[]>();
    const [clickedExceptions, setClickedExceptions] = useState<ExceptionResponse[]>();
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);
    const [clickedDate, setClickedDate] = useState<string>();
    const [exceptionFilterType, setExceptionFilterType] = useState<ExceptionRiskScoreFilter>(ExceptionRiskScoreFilter.ALL);

    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.NEXT_THREE_MONTHS);

    useEffect(() => {
        const exceptionPriorityFilter = (exception: ExceptionResponse): boolean => {
            if (exceptionFilterType === ExceptionRiskScoreFilter.ALL) {
                return true;
            } else if (exceptionFilterType === ExceptionRiskScoreFilter.HIGH) {
                return exception.risk_score === ExceptionRiskScore.HIGH;
            } else if (exceptionFilterType === ExceptionRiskScoreFilter.MODERATE_HIGH) {
                return exception.risk_score === ExceptionRiskScore.MODERATE_HIGH;
            } else if (exceptionFilterType === ExceptionRiskScoreFilter.MODERATE) {
                return exception.risk_score === ExceptionRiskScore.MODERATE;
            } else if (exceptionFilterType === ExceptionRiskScoreFilter.LOW_MODERATE) {
                return exception.risk_score === ExceptionRiskScore.LOW_MODERATE;
            } else if (exceptionFilterType === ExceptionRiskScoreFilter.LOW) {
                return exception.risk_score === ExceptionRiskScore.LOW;
            } else {
                return true;
            }
        };
        if (dashboardExceptions) {
            const dashboardExceptionsByRiskScore = dashboardExceptions.filter(exceptionPriorityFilter);

            const months = getMonths(dateFilterHookValues.selection.dateRange.startDate, dateFilterHookValues.selection.dateRange.endDate);

            const totalExceptionsByMonth = months.map((month) => {
                return dashboardExceptionsByRiskScore.filter((exceptions) => moment(exceptions.expiration_date).format('MMM YY') === month).length;
            });

            const overDueExceptions = dashboardExceptionsByRiskScore.filter((exception) => moment(exception.expiration_date).isBefore(moment()));

            setExceptionsByMonth([overDueExceptions.length, ...totalExceptionsByMonth]);
            setMonthLabel(['Overdue', ...months]);
        }
    }, [dashboardExceptions, exceptionFilterType, dateFilterHookValues]);

    const handleChartClick = (item: string) => {
        const currentFilteredExceptions = dashboardExceptions?.filter((exception) => {
            if (exceptionFilterType === ExceptionRiskScoreFilter.ALL) {
                return true;
            } else if (exceptionFilterType === ExceptionRiskScoreFilter.HIGH) {
                return exception.risk_score === ExceptionRiskScore.HIGH;
            } else if (exceptionFilterType === ExceptionRiskScoreFilter.MODERATE_HIGH) {
                return exception.risk_score === ExceptionRiskScore.MODERATE_HIGH;
            } else if (exceptionFilterType === ExceptionRiskScoreFilter.MODERATE) {
                return exception.risk_score === ExceptionRiskScore.MODERATE;
            } else if (exceptionFilterType === ExceptionRiskScoreFilter.LOW_MODERATE) {
                return exception.risk_score === ExceptionRiskScore.LOW_MODERATE;
            } else if (exceptionFilterType === ExceptionRiskScoreFilter.LOW) {
                return exception.risk_score === ExceptionRiskScore.LOW;
            } else {
                return true;
            }
        });

        if (currentFilteredExceptions) {
            const clickedItem = item.split(' | ');
            const clickedDate = toTitleCase(clickedItem[0]);

            setClickedExceptions(getOpenExceptionsPerMonth(currentFilteredExceptions, clickedDate));

            if (clickedDate === 'Overdue') {
                const overdueExceptions = currentFilteredExceptions.filter((exception) => moment(exception.expiration_date).isBefore(moment()));

                setClickedExceptions(overdueExceptions);
            } else {
                setClickedExceptions(getOpenExceptionsPerMonth(currentFilteredExceptions, clickedDate));
            }

            setClickedDate(clickedDate);
            setModalState(Modals.DISPLAY_OPEN_EXCEPTIONS);
        }
    };

    const handleRiskScoreChange = (value: ChangeEventType, formFieldId: string) => {
        if (isValidExceptionRiskScoreFilter(value)) {
            setExceptionFilterType(value as ExceptionRiskScoreFilter);
        }
    };

    if (props.exceptionsError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Exception Tracking
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {props.exceptionsError}
                </Text>
            </>
        );
    }

    if (props.organizationsError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Exception Tracking
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {props.organizationsError}
                </Text>
            </>
        );
    }

    return (
        <>
            {modalState === Modals.CUSTOM_DATE && <CustomDateRangeModal datesRestriction="futureDatesOnly" hideModal={() => setModalState(Modals.NONE)} dateFilterHookValues={dateFilterHookValues} />}
            {modalState === Modals.DISPLAY_OPEN_EXCEPTIONS && clickedDate && clickedExceptions && <TrackingExceptionsDisplayModal selectedException={clickedExceptions} date={clickedDate} hideModal={() => setModalState(Modals.NONE)} dashboardType={props.dashboardType} organizations={props.organizations} />}
            <div className={styles.cellHeader}>
                <Text variant="Header2" color="white">
                    Exception Tracking
                </Text>
                <div className={styles.filters}>
                    <FormFieldSelectDark formFieldId="exceptionRiskScore" formFieldLabel="Risk Score..." options={ExceptionRiskScoreFilterOptions} selectedOption={exceptionFilterType} handleChange={handleRiskScoreChange} />
                    <FormFieldDateOptionSelect formFieldId="date" options={DefaultUpcomingDateRangeOptions} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />
                </div>
            </div>
            {monthLabel && exceptionsByMonth ? <BarChart onChartClick={handleChartClick} xAxisLabels={monthLabel} bars={{ name: 'Total Due', color: CSSColors.LIGHT_BLUE, data: exceptionsByMonth }} yAxisInfo={[{ name: 'Open Exceptions by Month', position: 'left', minInterval: 1 }]} /> : <Skeleton variant="rounded" width={'100%'} height={300} />}
        </>
    );
};
